import { useState } from 'react';
import { Share2, Trophy, Flame, Calendar, Star, Target, Heart, Zap } from 'lucide-react';
import { toast } from 'sonner';
import ShareMoodCard from './ShareMoodCard';

// Achievement definitions
const ACHIEVEMENTS = {
  first_checkin: {
    id: 'first_checkin',
    title: 'First Step',
    description: 'Logged your first emotional check-in',
    icon: Star,
    color: '#FFD700',
    requirement: (stats) => stats.total_entries >= 1
  },
  streak_3: {
    id: 'streak_3',
    title: 'Building Momentum',
    description: '3-day check-in streak',
    icon: Flame,
    color: '#FF6B35',
    requirement: (stats) => stats.streak >= 3
  },
  streak_7: {
    id: 'streak_7',
    title: 'Week Warrior',
    description: '7-day check-in streak',
    icon: Flame,
    color: '#FF4500',
    requirement: (stats) => stats.streak >= 7
  },
  streak_30: {
    id: 'streak_30',
    title: 'Monthly Master',
    description: '30-day check-in streak',
    icon: Trophy,
    color: '#FFD700',
    requirement: (stats) => stats.streak >= 30
  },
  explorer_5: {
    id: 'explorer_5',
    title: 'Emotion Explorer',
    description: 'Explored 5 different emotion categories',
    icon: Target,
    color: '#4ECDC4',
    requirement: (stats) => stats.unique_categories >= 5
  },
  explorer_10: {
    id: 'explorer_10',
    title: 'Emotional Architect',
    description: 'Explored 10 different emotion categories',
    icon: Target,
    color: '#2ECC71',
    requirement: (stats) => stats.unique_categories >= 10
  },
  consistent_week: {
    id: 'consistent_week',
    title: 'Consistency Champion',
    description: 'Checked in every day this week',
    icon: Calendar,
    color: '#9B59B6',
    requirement: (stats) => stats.weekly_checkins >= 7
  },
  self_aware: {
    id: 'self_aware',
    title: 'Self-Awareness Star',
    description: 'Added notes to 10 check-ins',
    icon: Heart,
    color: '#E74C3C',
    requirement: (stats) => stats.entries_with_notes >= 10
  },
  body_mind_10: {
    id: 'body_mind_10',
    title: 'Mind-Body Connection',
    description: 'Tracked body sensations 10 times',
    icon: Zap,
    color: '#3498DB',
    requirement: (stats) => stats.entries_with_sensations >= 10
  }
};

const AchievementBadge = ({ achievement, unlocked, onShare }) => {
  const Icon = achievement.icon;
  
  return (
    <div 
      className={`relative p-4 rounded-2xl border-2 transition-all duration-300 ${
        unlocked 
          ? 'bg-gradient-to-br from-white to-stone-50 border-stone-200 hover:shadow-lg cursor-pointer hover:scale-[1.02]' 
          : 'bg-stone-100 border-stone-200 opacity-50'
      }`}
      onClick={() => unlocked && onShare(achievement)}
    >
      <div className="flex items-start gap-3">
        <div 
          className={`w-12 h-12 rounded-xl flex items-center justify-center ${unlocked ? '' : 'grayscale'}`}
          style={{ backgroundColor: unlocked ? `${achievement.color}20` : '#E5E7EB' }}
        >
          <Icon 
            className="w-6 h-6" 
            style={{ color: unlocked ? achievement.color : '#9CA3AF' }}
          />
        </div>
        <div className="flex-1">
          <h4 className={`font-semibold text-sm ${unlocked ? 'text-warm-charcoal' : 'text-gray-400'}`}>
            {achievement.title}
          </h4>
          <p className={`text-xs mt-0.5 ${unlocked ? 'text-warm-charcoal/60' : 'text-gray-400'}`}>
            {achievement.description}
          </p>
        </div>
        {unlocked && (
          <Share2 className="w-4 h-4 text-hunter-green opacity-50 group-hover:opacity-100" />
        )}
      </div>
      {unlocked && (
        <div className="absolute -top-1 -right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
          <span className="text-white text-xs">✓</span>
        </div>
      )}
    </div>
  );
};

const AchievementsPanel = ({ stats }) => {
  const [selectedAchievement, setSelectedAchievement] = useState(null);

  // Calculate which achievements are unlocked
  const achievementStatus = Object.values(ACHIEVEMENTS).map(achievement => ({
    ...achievement,
    unlocked: achievement.requirement(stats || {
      total_entries: 0,
      streak: 0,
      unique_categories: 0,
      weekly_checkins: 0,
      entries_with_notes: 0,
      entries_with_sensations: 0
    })
  }));

  const unlockedCount = achievementStatus.filter(a => a.unlocked).length;

  const handleShare = async (achievement) => {
    const shareText = `🏆 Achievement Unlocked: ${achievement.title}!\n\n${achievement.description}\n\nTracking my emotional wellness journey with MyMojo 🧘‍♀️\n#EmotionalWellness #Achievement #MyMojo`;
    
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Achievement: ${achievement.title}`,
          text: shareText,
          url: window.location.origin
        });
        toast.success('Shared your achievement!');
      } catch (error) {
        if (error.name !== 'AbortError') {
          // Fallback to clipboard
          try {
            await navigator.clipboard.writeText(`${shareText}\n\n${window.location.origin}`);
            toast.success('Achievement copied to clipboard!');
          } catch (e) {
            console.error('Share failed:', error);
          }
        }
      }
    } else {
      // Fallback to clipboard
      try {
        await navigator.clipboard.writeText(`${shareText}\n\n${window.location.origin}`);
        toast.success('Achievement copied to clipboard!');
      } catch (e) {
        toast.error('Failed to share');
      }
    }
  };

  return (
    <div className="zen-card p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-yellow-500" />
          <h3 className="text-lg font-semibold" style={{ fontFamily: 'Playfair Display, serif' }}>
            Achievements
          </h3>
        </div>
        <span className="text-sm text-warm-charcoal/60">
          {unlockedCount}/{Object.keys(ACHIEVEMENTS).length} unlocked
        </span>
      </div>

      <div className="space-y-3">
        {achievementStatus.slice(0, 4).map((achievement) => (
          <AchievementBadge
            key={achievement.id}
            achievement={achievement}
            unlocked={achievement.unlocked}
            onShare={handleShare}
          />
        ))}
      </div>

      {achievementStatus.length > 4 && (
        <button className="w-full mt-4 text-sm text-hunter-green hover:underline">
          View all achievements →
        </button>
      )}
    </div>
  );
};

export { AchievementsPanel, ACHIEVEMENTS };
export default AchievementsPanel;
